/**
 * Edit Network Modal Component
 * Allows updating network credentials including the XML API key
 */

import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Eye, EyeOff, Loader2, Save, Tag, Plus, ExternalLink, Search } from 'lucide-react';
import { useUpdateNetwork, useNetworkDetails } from '../../hooks/useNetworksData';
import type { NetworkCardData } from '../../types/networks.types';
import { toast } from 'sonner';

interface EditNetworkModalProps {
  network: NetworkCardData;
  open: boolean;
  onClose: () => void;
}

export const EditNetworkModal: React.FC<EditNetworkModalProps> = ({
  network,
  open,
  onClose
}) => {
  const [accountName, setAccountName] = useState(network.account_name);
  const [username, setUsername] = useState(network.username || '');
  const [email, setEmail] = useState('');  // For ReferOn - requires email for login
  const [loginUrl, setLoginUrl] = useState('');  // Affiliate portal login URL
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [tags, setTags] = useState<string[]>(network.tags || []);
  const [newTag, setNewTag] = useState('');
  const [requires2FA, setRequires2FA] = useState<boolean | null>(network.requires_2fa ?? null);
  const [currency, setCurrency] = useState(network.currency || '');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [trackerCheck, setTrackerCheck] = useState<boolean>(Boolean(network.metadata?.tracker_check));
  const [currencyChecking, setCurrencyChecking] = useState(false);
  const [currencyDetection, setCurrencyDetection] = useState<{
    currency?: string;
    confidence?: string;
    source?: string;
    details?: string;
    error?: string;
  } | null>(null);
  const updateMutation = useUpdateNetwork();
  
  // Currency options - expanded for iGaming (includes Latin American, Eastern European, Asian currencies)
  const currencyOptions = [
    'EUR', 'USD', 'GBP', 'SEK', 'NOK', 'DKK', 'CHF', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'ISK',
    'CAD', 'AUD', 'NZD', 'BRL', 'MXN', 'COP', 'CLP', 'PEN', 'ARS',
    'JPY', 'CNY', 'INR', 'KRW', 'HKD', 'SGD', 'THB', 'IDR', 'MYR', 'PHP', 'TWD', 'VND', 'BDT', 'PKR',
    'ZAR', 'TRY', 'RUB', 'UAH', 'GEL', 'KZT', 'ILS', 'EGP', 'NGN', 'KES', 'MAD', 'TND'
  ];
  
  // Check if this is a ReferOn account
  const isReferOn = network.platform_provider?.toLowerCase().includes('referon') ||
                    network.login_url?.toLowerCase().includes('referon');
  
  // Check if this is an Affilka/Softswiss account
  const isAffilka = network.platform_provider?.toLowerCase().includes('affilka') ||
                    network.platform_provider?.toLowerCase().includes('softswiss') ||
                    network.platform_name?.toLowerCase().includes('affilka') ||
                    network.platform_name?.toLowerCase().includes('softswiss');
  
  // Check if this is a MyAffiliates account
  const isMyAffiliates = network.platform_provider?.toLowerCase().includes('myaffiliates') ||
                         network.platform_name?.toLowerCase().includes('myaffiliates') ||
                         network.login_url?.toLowerCase().includes('myaffiliates');
  
  const isRavenTrack = network.platform_provider?.toLowerCase().includes('raventrack') ||
                       network.platform_name?.toLowerCase().includes('raventrack');

  const supportsCampaignFilter = isAffilka || isMyAffiliates;
  // Platforms whose adapter implements fetchTrackedPlayers (shared/platforms)
  const supportsTrackerCheck = isMyAffiliates;
  
  // Fetch detailed account info including login_url
  const { data: details, isLoading: detailsLoading } = useNetworkDetails(open ? network.id : '');

  // Suggested tags for quick add
  const suggestedTags = ['Direct', 'Raketech', 'Kiickr', 'Premium', 'Network'];

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      // Only reset form fields when modal first opens
      setAccountName(network.account_name);
      setUsername(network.username || '');
      setLoginUrl(network.login_url || '');
      setTags(network.tags || []);
      setNewTag('');
      setPassword('');
      setApiKey('');
      setShowPassword(false);
      setShowApiKey(false);
      setRequires2FA(network.requires_2fa ?? null);
      setCurrency(network.currency || '');
      setCampaignFilter('');
      setTrackerCheck(Boolean(network.metadata?.tracker_check));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Intentionally only depend on open, not network

  // Update username, email, loginUrl, and campaignFilter when details load
  useEffect(() => {
    if (open && details) {
      setUsername(details.username || network.username || '');
      setEmail(details.email || '');
      setLoginUrl(details.login_url || network.login_url || '');
      setCampaignFilter((details as any).campaign_filter || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]); // Only update when details changes

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleCheckCurrency = async () => {
    setCurrencyChecking(true);
    setCurrencyDetection(null);
    try {
      // Extract JWT token from Zustand-persisted auth state
      let token: string | null = null;
      try {
        const auth = localStorage.getItem('adwstats-auth');
        if (auth) {
          const parsed = JSON.parse(auth);
          token = parsed.state?.token || null;
        }
      } catch { /* ignore parse errors */ }
      const response = await fetch(`/api/networks/${network.id}/check-currency`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json() as {
        success: boolean;
        currency?: string;
        confidence?: string;
        source?: string;
        details?: string;
        error?: string;
      };
      setCurrencyDetection(data);
      
      if (data.success && data.currency) {
        setCurrency(data.currency);
        toast.success(`Detected currency: ${data.currency} (${data.confidence} confidence)`);
      } else {
        toast.error(data.error || data.details || 'Could not detect currency');
      }
    } catch (error: any) {
      toast.error('Failed to check currency: ' + (error.message || 'Unknown error'));
      setCurrencyDetection({ error: error.message });
    } finally {
      setCurrencyChecking(false);
    }
  };

  const handleSave = async () => {
    try {
      // Read from DOM as fallback for browser automation compatibility
      // React controlled components may not capture programmatic input in some cases
      const apiKeyInput = document.querySelector<HTMLInputElement>('input[placeholder*="statistic token"], input[placeholder*="existing key"]');
      const passwordInput = document.querySelector<HTMLInputElement>('input[placeholder="Enter new password"]');
      
      const effectiveApiKey = apiKey || apiKeyInput?.value || '';
      const effectivePassword = password || passwordInput?.value || '';
      
      // Debug: Log what we're sending
      const updates: Record<string, any> = {
        account_name: accountName,
        username: username || undefined,
        email: email || undefined,
        login_url: loginUrl || undefined,
        password: effectivePassword || undefined,
        api_key: effectiveApiKey || undefined,
        tags,
        requires_2fa: requires2FA,
        currency: currency || undefined,
        ...(supportsCampaignFilter ? { campaign_filter: campaignFilter || null } : {}),
        ...(supportsTrackerCheck ? { tracker_check: trackerCheck } : {}),
      };
      
      console.log('[EditNetworkModal] Saving updates:', JSON.stringify({
        password_present: !!effectivePassword,
        password_length: effectivePassword?.length || 0,
        password_source: password ? 'react_state' : (passwordInput?.value ? 'dom_fallback' : 'none'),
        api_key_present: !!effectiveApiKey,
        api_key_length: effectiveApiKey?.length || 0,
        api_key_value: effectiveApiKey ? `${effectiveApiKey.substring(0, 8)}...` : 'EMPTY',
        api_key_source: apiKey ? 'react_state' : (apiKeyInput?.value ? 'dom_fallback' : 'none'),
        updates_keys: Object.keys(updates).filter(k => updates[k as keyof typeof updates] !== undefined),
        full_updates: updates
      }, null, 2));
      
      await updateMutation.mutateAsync({
        networkId: network.id,
        updates
      });
      toast.success('Network updated successfully');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update network');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md z-50 max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Edit Network
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-5">
            {/* Platform Info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
              {network.platform_logo && (
                <img
                  src={network.platform_logo}
                  alt={network.platform_name}
                  className="h-8 object-contain"
                />
              )}
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {network.platform_name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  ID: {network.id.slice(0, 8)}...
                </p>
              </div>
            </div>

            {/* Affiliate Portal URL and Debug Info */}
            {/* Login URL - Editable */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Login URL
                <span className="text-gray-400 text-xs ml-2">(affiliate portal login page)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={loginUrl}
                  onChange={(e) => setLoginUrl(e.target.value)}
                  placeholder="https://affiliate.example.com/login"
                  className="flex-1 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
                {loginUrl && (
                  <a
                    href={loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>

            {detailsLoading ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                <span className="text-sm text-blue-600 dark:text-blue-400">Loading account details...</span>
              </div>
            ) : details ? (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-2">
                {/* Debug: Stored Credentials */}
                <div className="pt-2 border-t border-blue-200 dark:border-blue-700">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                    Stored Credentials (Debug):
                  </p>
                  <div className="text-xs text-blue-600 dark:text-blue-400 space-y-1 font-mono">
                    <p>Username: {details.username || 'NOT SET'}</p>
                    <p>Password: {details.password ? (
                      <span className="text-green-600 dark:text-green-400">
                        ✓ SET ({details.password.length} chars): {details.password.substring(0, 3)}...{details.password.slice(-3)}
                      </span>
                    ) : (
                      <span className="text-red-500 dark:text-red-400">✗ NOT SET</span>
                    )}</p>
                    <p>API Key: {details.api_key ? (
                      <span className="text-green-600 dark:text-green-400">
                        ✓ SET ({details.api_key.length} chars)
                      </span>
                    ) : (
                      <span className="text-gray-400">not set</span>
                    )}</p>
                    {details.affiliate_system_name && (
                      <p>Platform: {details.affiliate_system_name}</p>
                    )}
                    {(details as any).credential_keys && (
                      <p>Keys: [{(details as any).credential_keys?.join(', ')}]</p>
                    )}
                  </div>
                </div>
                
                {details.sync_from_date && (
                  <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                    Sync from: {details.sync_from_date}
                  </p>
                )}
              </div>
            ) : null}

            {/* Account Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Account Name
              </label>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={network.username || 'Enter username'}
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Email Address (optional, required for ReferOn) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
                {isReferOn ? (
                  <span className="text-amber-500 text-xs ml-2">(required for ReferOn login)</span>
                ) : (
                  <span className="text-gray-400 text-xs ml-2">(optional)</span>
                )}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isReferOn ? "Enter email address for ReferOn login" : "Enter email address (optional)"}
                className={`w-full px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border ${
                  isReferOn 
                    ? 'border-amber-300 dark:border-amber-600 focus:ring-amber-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:ring-emerald-500'
                } rounded-lg focus:outline-none focus:ring-2 focus:border-transparent`}
              />
              {isReferOn && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  ReferOn requires email for login. Use the email associated with your ReferOn account.
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
                <span className="text-gray-400 text-xs ml-2">(leave empty to keep existing)</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* API Key / Statistic Token */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isAffilka ? 'Statistic Token' : isRavenTrack ? 'API Access Token' : 'XML API Key'}
                <span className="text-gray-400 text-xs ml-2">
                  {isAffilka || isRavenTrack ? '(required for API access)' : '(for daily statistics)'}
                </span>
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={isAffilka 
                    ? 'Enter 32-character statistic token' 
                    : 'Leave empty to keep existing key'}
                  className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {isAffilka 
                  ? 'Find this in your Affilka portal: My Account → Account Info → Statistic Token'
                  : isRavenTrack
                    ? 'In the affiliate portal: Account → API → Create New Token, then copy the access token shown once on creation (not the Token ID in the list)'
                    : 'Find this in your affiliate portal under "XML Feeds" section'}
              </p>
            </div>

            {/* 2FA Required (Affilka only) */}
            {isAffilka && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  2FA Required
                  <span className="text-gray-400 text-xs ml-2">(for login authentication)</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setRequires2FA(true)}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border ${
                      requires2FA === true
                        ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-500 text-amber-700 dark:text-amber-300'
                        : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Yes (2FA)
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequires2FA(false)}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border ${
                      requires2FA === false
                        ? 'bg-green-100 dark:bg-green-900/30 border-green-500 text-green-700 dark:text-green-300'
                        : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    No (Direct)
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequires2FA(null)}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border ${
                      requires2FA === null
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300'
                        : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Auto
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {requires2FA === true ? 'Account requires 2FA from Affiliation Cloud' :
                   requires2FA === false ? 'Account can login directly without 2FA' :
                   'System will auto-detect based on login response'}
                </p>
              </div>
            )}

            {/* Campaign/Brand Filter (shared accounts) */}
            {supportsCampaignFilter && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {isMyAffiliates ? 'Channel Filter' : 'Campaign Filter'}
                  <span className="text-gray-400 text-xs ml-2">(for shared accounts)</span>
                </label>
                <input
                  type="text"
                  value={campaignFilter}
                  onChange={(e) => setCampaignFilter(e.target.value)}
                  placeholder={isMyAffiliates ? 'e.g. 6 or 6,8 (channel IDs from portal)' : 'e.g. 21934 or 21934,11075'}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isMyAffiliates
                    ? 'Channel ID(s) from the MyAffiliates portal (found in the "Filter by channel" dropdown). Supports comma-separated IDs for multiple channels (e.g. "6,8"). Leave empty to sync all.'
                    : 'Comma-separated Affilka campaign IDs. When set, only commissions from these campaigns are synced. Leave empty to sync all.'}
                </p>
              </div>
            )}

            {/* Tracker check: fill missing tracker postbacks from the player reports */}
            {supportsTrackerCheck && (
              <div>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tracker check</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={trackerCheck}
                    onClick={() => setTrackerCheck(!trackerCheck)}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                      trackerCheck ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${
                        trackerCheck ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Every 3 hours, read the customer registrations (Payload = our click id) and first deposits
                  from the portal and add any REG / FTD the tracker never got as a postback. Uses the portal
                  username and password above.
                </p>
              </div>
            )}

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Account Currency
                <span className="text-gray-400 text-xs ml-2">(for reporting)</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="flex-1 px-4 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Select currency...</option>
                  {currencyOptions.map((cur) => (
                    <option key={cur} value={cur}>{cur}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleCheckCurrency}
                  disabled={currencyChecking}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap"
                  title="Auto-detect currency from operator API"
                >
                  {currencyChecking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Detect
                </button>
              </div>
              {currencyDetection && (
                <div className={`mt-2 p-2 rounded-lg text-xs ${
                  currencyDetection.error 
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                    : currencyDetection.confidence === 'high'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                    : currencyDetection.confidence === 'medium'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                    : 'bg-gray-50 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400'
                }`}>
                  {currencyDetection.error ? (
                    <p>Detection failed: {currencyDetection.error}</p>
                  ) : (
                    <>
                      <p className="font-medium">
                        Detected: {currencyDetection.currency} 
                        <span className="ml-1 opacity-75">({currencyDetection.confidence} confidence)</span>
                      </p>
                      {currencyDetection.details && (
                        <p className="mt-0.5 opacity-75">{currencyDetection.details}</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Tag className="w-4 h-4" />
                Tags
                <span className="text-gray-400 text-xs">(for filtering & reporting)</span>
              </label>
              
              {/* Current Tags */}
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-emerald-900 dark:hover:text-emerald-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Add Tag Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTag(newTag)}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
                <button
                  onClick={() => addTag(newTag)}
                  disabled={!newTag.trim()}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              {/* Suggested Tags */}
              <div className="mt-2">
                <p className="text-xs text-gray-400 mb-1">Quick add:</p>
                <div className="flex flex-wrap gap-1">
                  {suggestedTags.filter(t => !tags.includes(t)).map((tag) => (
                    <button
                      key={tag}
                      onClick={() => addTag(tag)}
                      className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

